'use client';

import type { I18n } from '@funkey/locales';
import { useMemo } from 'react';
import { Icon, type IconName } from '~/components/primitives';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { useI18n } from '~/features/i18n';
import { I18N_CONF } from '@funkey/locales/config';
import { usePostHog } from 'posthog-js/react';
import { cn } from '~/utils';

type FaqOption = {
    id: string;
    label: string;
    query: string;
    icon: IconName;
};

function buildFaqOptions(i18n: I18n): FaqOption[] {
    return [
        {
            id: 'why-funkey',
            label: i18n._('faq.cart.option.whyFunkey.title'),
            query: i18n._('faq.cart.option.whyFunkey.query'),
            icon: 'aboutFunkey',
        },
        {
            id: 'invoicing',
            label: i18n._('faq.cart.option.invoicing.title'),
            query: i18n._('faq.cart.option.invoicing.query'),
            icon: 'wallet',
        },
        {
            id: 'transport',
            label: i18n._('faq.cart.option.transport.title'),
            query: i18n._('faq.cart.option.transport.query'),
            icon: 'move',
        },
        {
            id: 'catering',
            label: i18n._('faq.cart.option.catering.title'),
            query: i18n._('faq.cart.option.catering.query'),
            icon: 'catering',
        },
        {
            id: 'location',
            label: i18n._('faq.cart.option.location.title'),
            query: i18n._('faq.cart.option.location.query'),
            icon: 'location',
        },
        {
            id: 'cancellation',
            label: i18n._('faq.cart.option.cancellation.title'),
            query: i18n._('faq.cart.option.cancellation.query'),
            icon: 'close',
        },
    ];
}

export const FaqDropdown: React.FC<{
    source: 'cart_sidebar' | 'wishlist_sidebar' | 'checkout_sidebar';
    className?: string;
}> = ({ source, className }) => {
    const i18n = useI18n();
    const posthog = usePostHog();
    const locale = i18n.locale;
    const faqOptions = useMemo(() => buildFaqOptions(i18n), [i18n, i18n.locale]);

    const handleSelect = (option: FaqOption) => {
        posthog.capture('faq_dropdown.question_clicked', {
            question: option.query,
            label: option.label,
            source,
            locale,
        });

        window.open(
            I18N_CONF.getTranslatedUrl('faq', {
                locale,
                searchParams: `q=${encodeURIComponent(option.query)}`,
            }),
            '_blank',
            'noopener,noreferrer',
        );
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'group flex w-full items-center justify-between rounded-xl border border-brown-200 bg-white px-4 py-3 text-left text-sm font-medium text-primary transition-colors hover:border-brown-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-salmon-500',
                        className,
                    )}
                >
                    <span className="flex items-center gap-2">
                        <Icon name="faq" size="sm" className="text-salmon-500" />
                        {i18n._('faq.cart.dropdown.prompt')}
                    </span>
                    <Icon
                        name="chevron-down"
                        size="xs"
                        className="text-blue-600 transition-transform group-data-[state=open]:rotate-180"
                    />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                sideOffset={4}
                className="w-[var(--radix-dropdown-menu-trigger-width)] rounded-xl border-brown-200 p-0"
            >
                {faqOptions.map(option => (
                    <DropdownMenuItem
                        key={option.id}
                        onSelect={() => handleSelect(option)}
                        className="cursor-pointer gap-3 rounded-none px-4 py-3 text-sm text-primary focus:bg-brown-50"
                    >
                        <Icon name={option.icon} size="sm" className="shrink-0 text-salmon-500" />
                        <span>{option.label}</span>
                        <Icon
                            name="external-link"
                            size="xs"
                            className="ml-auto shrink-0 text-blue-400"
                        />
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
